import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { ringColor } from "./accents.js";
import { GuideCard } from "./chrome/guide-card.js";
import { Icon } from "./chrome/icons.js";
import { useObjectUrls } from "./chrome/object-urls.js";
import { RoleShift } from "./chrome/role-shift.js";
import { nowAt } from "./clock.js";
import { roleForRoute, roleShift, stepCopy } from "./guide.js";
import { allPhotos, newestFirst, visiblePhotos } from "./photos.js";
import { clientHref, seededState, suggestedBrand } from "./prefill.js";
import { guardRoute } from "./routes.js";
import { SAMPLE_IDS, seedSrc } from "./seed.js";
import { AlbumScreen } from "./screens/album.js";
import { BrandScreen } from "./screens/brand.js";
import { IntroScreen } from "./screens/intro.js";
import { CameraScreen } from "./screens/camera.js";
import { FinishScreen } from "./screens/finish.js";
import { GalleryScreen } from "./screens/gallery.js";
import { InviteScreen } from "./screens/invite.js";
import { ModerationScreen } from "./screens/moderation.js";
import { PanelScreen } from "./screens/panel.js";
import { WallScreen } from "./screens/wall.js";
import {
  go,
  replaceRoute,
  useDemo,
  useHashRoute,
  useNow,
  type DemoController,
  type DemoSeed,
} from "./state.js";
import { openStore, type DemoStore } from "./store.js";
import { PHOTO_QUOTA } from "./types.js";
import { demoWhatsappHref } from "./whatsapp.js";

const openBrowserStore = () => openStore();

const seedFromLink: DemoSeed = (loaded, now) => seededState(window.location.search, loaded, now);

export interface DemoAppProps {
  readonly open?: () => Promise<DemoStore>;
  readonly seed?: DemoSeed;
}

export function DemoApp({ open = openBrowserStore, seed = seedFromLink }: DemoAppProps) {
  const demo = useDemo(open, seed);
  return demo ? <DemoView demo={demo} /> : <p class="d-boot">Carregando a demonstração…</p>;
}

interface DemoViewProps {
  readonly demo: DemoController;
}

function DemoView({ demo }: DemoViewProps) {
  const { state, dispatch } = demo;
  const route = useHashRoute();
  const target = guardRoute(route, state);
  const now = useNow();
  const current = nowAt(state.startedAt, now);
  const today = new Date(now);
  const urls = useObjectUrls(state.photos);
  const logoUrl =
    useObjectUrls(state.brand?.logo ? [{ id: "logo", image: state.brand.logo }] : []).get("logo") ??
    null;
  const photos = allPhotos(state, urls);
  const visible = visiblePhotos(photos, state.hidden);
  const mine = newestFirst(photos.filter((photo) => photo.mine));
  const audience = state.audience;
  const accent = ringColor(state.brand?.accent ?? "green");
  const whatsapp = demoWhatsappHref(state.brand?.name ?? null);
  const copy = stepCopy(state.guide.step, audience);
  const left = PHOTO_QUOTA - state.photos.length;
  const [shift, setShift] = useState<string | null>(null);
  const role = roleForRoute(target, state.guide.step);
  const phoneRoute =
    target === "convite" || target === "camera" || target === "galeria" || target === "album";
  const lastRole = useRef(role);

  useEffect(() => {
    if (lastRole.current !== role) {
      lastRole.current = role;
      setShift(roleShift(role, audience));
    }
  }, [role, audience]);

  useEffect(() => {
    if (target !== route) {
      replaceRoute(target);
    }
  }, [target, route]);

  const restart = () => {
    if (window.confirm("Recomeçar apaga o evento de exemplo e as suas fotos. Continuar?")) {
      void demo.restart().then(() => go("marca"));
    }
  };

  const next = () => {
    if (state.guide.step === "panel") {
      dispatch({ type: "invite-opened" });
      go("convite");
    } else if (state.guide.step === "wall") {
      dispatch({ type: "wall-seen" });
      go(audience === "client" ? "album" : "moderacao");
    } else if (state.guide.step === "album") {
      dispatch({ type: "album-seen" });
      go("fim");
    }
  };

  const brand = state.brand;
  let screen: JSX.Element;
  if (target === "inicio") {
    screen = (
      <IntroScreen
        hostName={audience === "client" ? (brand?.name ?? null) : null}
        onStart={() => go(audience === "client" ? "convite" : "marca")}
      />
    );
  } else if (target === "marca" || !brand) {
    screen = (
      <BrandScreen
        initial={suggestedBrand(window.location.search, brand)}
        onCreate={(created) => {
          dispatch({ type: "brand-created", brand: created });
          go("painel");
        }}
      />
    );
  } else if (target === "painel") {
    screen = (
      <PanelScreen
        brand={brand}
        photos={visible}
        guests={new Set(photos.map((photo) => photo.author)).size}
        current={current}
        today={today}
        onInvite={() => {
          dispatch({ type: "invite-opened" });
          go("convite");
        }}
        onWall={() => go("telao")}
        onModerate={() => go("moderacao")}
        onEditBrand={() => go("marca")}
      />
    );
  } else if (target === "convite") {
    screen = (
      <InviteScreen
        brand={brand}
        logoUrl={logoUrl}
        accent={accent}
        photoCount={visible.length}
        current={current}
        today={today}
        initial={state.guest}
        onJoin={(guest) => {
          dispatch({ type: "guest-joined", guest });
          go("camera");
        }}
      />
    );
  } else if (target === "camera") {
    screen = (
      <CameraScreen
        eventName={brand.eventName}
        accent={accent}
        left={left}
        lastUrl={mine[0]?.small ?? null}
        tipSeen={state.tipSeen}
        media={navigator.mediaDevices}
        samples={SAMPLE_IDS.map((id) => seedSrc(id, "l"))}
        onTipSeen={() => dispatch({ type: "tip-seen" })}
        onSend={(captured) => {
          dispatch({
            type: "photo-sent",
            photo: {
              id: `me-${Date.now()}`,
              image: captured.image,
              width: captured.width,
              height: captured.height,
              author: state.guest ? `${state.guest.first} ${state.guest.last}` : "Convidado",
              at: nowAt(state.startedAt, Date.now()),
            },
          });
          if (state.guide.step === "camera") {
            go("telao");
          }
        }}
      />
    );
  } else if (target === "galeria") {
    screen = <GalleryScreen mine={mine} left={left} accent={accent} />;
  } else if (target === "album") {
    screen = (
      <AlbumScreen
        brand={brand}
        logoUrl={logoUrl}
        accent={accent}
        photos={visible}
        today={today}
        client={role === "client"}
      />
    );
  } else if (target === "telao") {
    screen = (
      <WallScreen
        brand={brand}
        logoUrl={logoUrl}
        accent={accent}
        photos={visible}
        current={current}
      />
    );
  } else if (target === "moderacao") {
    screen = (
      <ModerationScreen
        photos={photos}
        hidden={state.hidden}
        onHide={(id) => dispatch({ type: "photo-hidden", id })}
        onShow={(id) => dispatch({ type: "photo-shown", id })}
      />
    );
  } else {
    screen = (
      <FinishScreen
        brand={brand}
        logoUrl={logoUrl}
        accent={accent}
        photos={visible}
        featured={mine[0] ?? null}
        whatsapp={whatsapp}
        audience={audience}
        shareUrl={clientHref(window.location.origin, brand)}
        onExplore={() => {
          if (!state.guide.collapsed) {
            dispatch({ type: "guide-toggled" });
          }
          go("painel");
        }}
        onRestart={restart}
      />
    );
  }

  return (
    <div
      class={target === "inicio" ? "d-app is-bare" : "d-app"}
      data-guide-step={!state.guide.collapsed && copy.route === target ? copy.step : undefined}
    >
      {target === "inicio" ? null : (
        <GuideCard
          guide={state.guide}
          audience={audience}
          route={target}
          whatsapp={whatsapp}
          onGo={go}
          onNext={next}
          onToggle={() => dispatch({ type: "guide-toggled" })}
          onRestart={restart}
        />
      )}
      <div class={phoneRoute ? "d-stage is-phone" : "d-stage"}>
        {phoneRoute ? <div class="d-phone">{screen}</div> : screen}
        {phoneRoute ? (
          <div class="d-rotate" data-theme="dark">
            <Icon name="switch" />
            <p class="d-rotate-title">Gire o celular</p>
            <p class="d-caption">A tela do convidado foi feita para o celular em pé.</p>
          </div>
        ) : null}
      </div>
      {shift ? <RoleShift role={role} text={shift} onDone={() => setShift(null)} /> : null}
    </div>
  );
}
